/** Minimal typed fetch wrapper. Uses relative URLs so edge Caddy routes /v1 to the API. */

export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;

  constructor(status: number, message: string, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

type JsonObject = Record<string, unknown>;
type RequestOpts = { auth?: boolean };

const TOKEN_KEY = "ini.access_token";

export function getToken(): string | null {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  window.localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(TOKEN_KEY);
}

async function request<T>(
  method: string,
  path: string,
  body?: JsonObject,
  opts: RequestOpts = {},
): Promise<T> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (opts.auth) {
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  const res = await fetch(path, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    let code: string | undefined;
    let msg = `${res.status} ${res.statusText}`;
    try {
      const data = (await res.json()) as
        | { error?: { code?: string; message?: string }; detail?: string };
      if (data?.error?.message) msg = data.error.message;
      else if (data?.detail) msg = data.detail;
      code = data?.error?.code;
    } catch {
      /* body wasn't JSON; keep status-line message */
    }
    throw new ApiError(res.status, msg, code);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string, opts?: RequestOpts) => request<T>("GET", path, undefined, opts),
  post: <T>(path: string, body?: JsonObject, opts?: RequestOpts) =>
    request<T>("POST", path, body, opts),
};

export type TokenResponse = {
  access_token: string;
  token_type: string;
};

export type UserMe = {
  id: string;
  email: string;
  is_active: boolean;
  is_superuser: boolean;
  is_verified: boolean;
};

export type AuthProviders = {
  email: boolean;
  google: boolean;
  signup: boolean;
};

export const auth = {
  signup: (email: string, password: string) =>
    api.post<TokenResponse>("/v1/auth/signup", { email, password }),
  login: (email: string, password: string) =>
    api.post<TokenResponse>("/v1/auth/login", { email, password }),
  me: () => api.get<UserMe>("/v1/auth/me", { auth: true }),
  providers: () => api.get<AuthProviders>("/v1/auth/providers"),
  googleAuthorize: () =>
    api.get<{ authorization_url: string }>("/v1/auth/google/authorize"),
  logout: () => clearToken(),
};

export type Conversation = {
  id: string;
  user_id: string;
  title: string;
  model: string;
  assistant_id: string | null;
  folder_id: string | null;
  created_at: string;
  updated_at: string;
};

export type ChatMessage = {
  id: string;
  conversation_id: string;
  user_id: string | null;
  role: "user" | "assistant" | "system" | "tool";
  content: string;
  model: string | null;
  created_at: string;
};

export type ConversationWithMessages = Conversation & { messages: ChatMessage[] };

export type SendMessageOut = {
  user_message: ChatMessage;
  assistant_message: ChatMessage;
};

export type ToolCallEvent = {
  id: string;
  name: string;
  arguments: Record<string, unknown>;
};

export type ToolResultEvent = {
  call_id: string;
  name: string;
  content: string;
  is_error: boolean;
};

export type StreamEvent =
  | { event: "user_message"; data: ChatMessage }
  | { event: "delta"; data: { text: string } }
  | { event: "assistant_message"; data: ChatMessage }
  | { event: "title"; data: { conversation_id: string; title: string } }
  | { event: "tool_call"; data: ToolCallEvent }
  | { event: "tool_result"; data: ToolResultEvent }
  | { event: "error"; data: { message: string; type: string } };

async function* _parseSse(response: Response): AsyncGenerator<StreamEvent> {
  if (!response.body) throw new ApiError(response.status, "No response body");
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const blocks = buffer.split("\n\n");
    buffer = blocks.pop() ?? "";
    for (const block of blocks) {
      if (!block.trim()) continue;
      let eventName = "message";
      const dataLines: string[] = [];
      for (const line of block.split("\n")) {
        if (line.startsWith("event: ")) eventName = line.slice(7).trim();
        else if (line.startsWith("data: ")) dataLines.push(line.slice(6));
      }
      const rawData = dataLines.join("\n");
      try {
        const parsed = JSON.parse(rawData);
        yield { event: eventName, data: parsed } as StreamEvent;
      } catch {
        yield {
          event: "error",
          data: { message: "Malformed SSE data", type: "ParseError" },
        };
      }
    }
  }
}

export const conversations = {
  list: () => api.get<Conversation[]>("/v1/conversations", { auth: true }),
  create: (opts: { title?: string; model?: string } = {}) =>
    api.post<Conversation>("/v1/conversations", opts, { auth: true }),
  get: (id: string) =>
    api.get<ConversationWithMessages>(`/v1/conversations/${id}`, { auth: true }),
  rename: (id: string, title: string) =>
    authedPatch<Conversation>(`/v1/conversations/${id}`, { title }),
  update: (id: string, body: { model?: string; assistant_id?: string | null }) =>
    authedPatch<Conversation>(`/v1/conversations/${id}`, body),
  remove: (id: string) => authedDelete(`/v1/conversations/${id}`),
  send: (id: string, content: string) =>
    api.post<SendMessageOut>(
      `/v1/conversations/${id}/messages`,
      { content },
      { auth: true },
    ),
  stream: async (id: string, content: string, signal?: AbortSignal) => {
    const token = getToken();
    const response = await fetch(`/v1/conversations/${id}/messages/stream`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ content }),
      signal,
    });
    if (!response.ok) {
      let msg = `${response.status} ${response.statusText}`;
      try {
        const data = await response.json();
        if (data?.error?.message) msg = data.error.message;
        else if (data?.detail) msg = data.detail;
      } catch {
        /* not json */
      }
      throw new ApiError(response.status, msg);
    }
    return _parseSse(response);
  },
};

export type AiModel = {
  id: string;
  name: string;
  provider: string;
  type: string;
  voices: string[] | null;
};

export type ModelsResponse = { models: AiModel[]; default: string | null };

export const ai = {
  models: (feature?: string) =>
    api.get<ModelsResponse>(
      `/v1/ai/models${feature ? `?feature=${encodeURIComponent(feature)}` : ""}`,
      { auth: true },
    ),
};

export type AdminModel = {
  key: string;
  provider: string;
  name: string;
  description: string | null;
  type: string;
  enabled: boolean;
  is_custom: boolean;
  position: number;
  config: Record<string, unknown>;
  created_at: string;
  updated_at: string;
};

export type AdminModelCreate = {
  key: string;
  provider: string;
  name: string;
  description?: string | null;
  type?: string;
  enabled?: boolean;
  position?: number;
  config?: Record<string, unknown>;
};

export type AdminModelUpdate = Partial<{
  name: string;
  description: string | null;
  type: string;
  enabled: boolean;
  position: number;
  config: Record<string, unknown>;
}>;

async function authedPostRaw<T>(path: string, body: unknown): Promise<T> {
  const token = getToken();
  const res = await fetch(path, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    try {
      const data = await res.json();
      if (data?.error?.message) msg = data.error.message;
      else if (data?.detail) msg = data.detail;
    } catch {
      /* not json */
    }
    throw new ApiError(res.status, msg);
  }
  return (await res.json()) as T;
}

async function authedPut<T>(path: string, body: Record<string, unknown>): Promise<T> {
  const token = getToken();
  const res = await fetch(path, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    try {
      const data = await res.json();
      if (data?.error?.message) msg = data.error.message;
      else if (data?.detail) msg = data.detail;
    } catch {
      /* not json */
    }
    throw new ApiError(res.status, msg);
  }
  return (await res.json()) as T;
}

async function authedPatch<T>(path: string, body: Record<string, unknown>): Promise<T> {
  const token = getToken();
  const res = await fetch(path, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    try {
      const data = await res.json();
      if (data?.error?.message) msg = data.error.message;
      else if (data?.detail) msg = data.detail;
    } catch {
      /* not json */
    }
    throw new ApiError(res.status, msg);
  }
  return (await res.json()) as T;
}

async function authedDelete(path: string): Promise<void> {
  const token = getToken();
  const res = await fetch(path, {
    method: "DELETE",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok && res.status !== 204) {
    let msg = `${res.status} ${res.statusText}`;
    try {
      const data = await res.json();
      if (data?.error?.message) msg = data.error.message;
      else if (data?.detail) msg = data.detail;
    } catch {
      /* not json */
    }
    throw new ApiError(res.status, msg);
  }
}

export type AdminRate = {
  model_key: string;
  credits_per_1k_input: number;
  credits_per_1k_output: number;
  updated_at: string;
};

export type AdminUsageEvent = {
  id: string;
  user_id: string | null;
  kind: string;
  model: string | null;
  provider: string | null;
  input_tokens: number | null;
  output_tokens: number | null;
  credits: number;
  created_at: string;
};

export type AdminUsageSummaryRow = {
  model: string | null;
  provider: string | null;
  calls: number;
  credits: number;
};

export type ProviderStatus = {
  provider: string;
  label: string;
  configured: boolean;
  source: "db" | "env" | null;
  masked_key: string | null;
  model_count: number;
};

export type RemoteModel = { id: string; name: string; type: string; added: boolean };
export type AddModelsResult = { added: string[]; skipped: string[] };

export type ReembedStatus = {
  pending_sources: number;
  total_sources: number;
  job_status: string | null;
};

export type FeatureDefault = {
  feature: string;
  label: string;
  model_type: string;
  default: string | null;
  fallback: string | null;
  reembed: ReembedStatus | null;
};

export const admin = {
  providers: {
    list: () => api.get<ProviderStatus[]>("/v1/sa/providers", { auth: true }),
    putKey: (provider: string, apiKey: string) =>
      authedPut<ProviderStatus>(`/v1/sa/providers/${provider}/key`, { api_key: apiKey }),
    liveModels: (provider: string) =>
      api.get<RemoteModel[]>(`/v1/sa/providers/${provider}/models`, { auth: true }),
    addModels: (provider: string, items: { id: string; name?: string; type: string }[]) =>
      authedPostRaw<AddModelsResult>(`/v1/sa/providers/${provider}/models`, items),
  },
  settings: {
    get: () => api.get<FeatureDefault[]>("/v1/sa/settings", { auth: true }),
    put: (changes: Record<string, string>) =>
      authedPut<FeatureDefault[]>("/v1/sa/settings", changes),
  },
  models: {
    list: () => api.get<AdminModel[]>("/v1/sa/models", { auth: true }),
    create: (body: AdminModelCreate) =>
      api.post<AdminModel>("/v1/sa/models", body, { auth: true }),
    update: (key: string, body: AdminModelUpdate) =>
      authedPatch<AdminModel>(`/v1/sa/models/${encodeURIComponent(key)}`, body),
    remove: (key: string) =>
      authedDelete(`/v1/sa/models/${encodeURIComponent(key)}`),
  },
  rates: {
    list: () => api.get<AdminRate[]>("/v1/sa/rates", { auth: true }),
    upsert: (body: { model_key: string; credits_per_1k_input: number; credits_per_1k_output: number }) =>
      api.post<AdminRate>("/v1/sa/rates", body, { auth: true }),
    update: (
      key: string,
      body: Partial<{ credits_per_1k_input: number; credits_per_1k_output: number }>,
    ) =>
      authedPatch<AdminRate>(`/v1/sa/rates/${encodeURIComponent(key)}`, body),
    remove: (key: string) => authedDelete(`/v1/sa/rates/${encodeURIComponent(key)}`),
  },
  usage: {
    list: (limit = 100) =>
      api.get<AdminUsageEvent[]>(`/v1/sa/usage?limit=${limit}`, { auth: true }),
    summary: () =>
      api.get<AdminUsageSummaryRow[]>("/v1/sa/usage/summary", { auth: true }),
  },
  users: {
    list: (search?: string, limit = 100, offset = 0) => {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      params.set("limit", String(limit));
      params.set("offset", String(offset));
      return api.get<{ items: AdminUserRow[]; total: number }>(
        `/v1/sa/users?${params.toString()}`,
        { auth: true },
      );
    },
    setFlags: (
      id: string,
      flags: Partial<{ is_active: boolean; is_superuser: boolean; is_verified: boolean }>,
    ) => authedPatch<AdminUserRow>(`/v1/sa/users/${id}`, flags),
  },
  stats: {
    get: () => api.get<AdminStats>("/v1/sa/stats", { auth: true }),
  },
  presets: {
    list: () => api.get<Preset[]>("/v1/sa/presets", { auth: true }),
    create: (body: AdminPresetCreate) =>
      api.post<Preset>("/v1/sa/presets", body, { auth: true }),
    update: (id: string, body: AdminPresetUpdate) =>
      authedPatch<Preset>(`/v1/sa/presets/${id}`, body),
    remove: (id: string) => authedDelete(`/v1/sa/presets/${id}`),
  },
};

export type AdminUserRow = {
  id: string;
  email: string;
  is_active: boolean;
  is_superuser: boolean;
  is_verified: boolean;
};

export type AdminStats = {
  total_users: number;
  active_users: number;
  verified_users: number;
  signups_last_7d: number;
  total_conversations: number;
  total_messages: number;
  top_models: {
    model: string;
    calls: number;
    input_tokens: number;
    output_tokens: number;
    credits: number;
  }[];
  signups_by_day: { date: string; count: number }[];
};

export type AdminPresetCreate = {
  slug: string;
  name: string;
  description?: string | null;
  category?: string;
  input_schema?: Record<string, unknown>;
  prompt_template: string;
  model?: string | null;
  enabled?: boolean;
  position?: number;
};

export type AdminPresetUpdate = Partial<{
  name: string;
  description: string | null;
  category: string;
  input_schema: Record<string, unknown>;
  prompt_template: string;
  model: string | null;
  enabled: boolean;
  position: number;
}>;

export type Source = {
  id: string;
  user_id: string;
  title: string;
  kind: string;
  file_id: string | null;
  url: string | null;
  content_preview: string | null;
  status: "pending" | "processing" | "ready" | "failed";
  status_error: string | null;
  chunk_count: number;
  token_count: number | null;
  embedding_model: string | null;
  created_at: string;
  updated_at: string;
};

async function authedUpload<T>(
  path: string,
  file: File,
  extra?: Record<string, string>,
): Promise<T> {
  const token = getToken();
  const form = new FormData();
  form.append("file", file);
  if (extra) for (const [k, v] of Object.entries(extra)) form.append(k, v);
  const res = await fetch(path, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    try {
      const data = await res.json();
      if (data?.error?.message) msg = data.error.message;
      else if (data?.detail) msg = data.detail;
    } catch {
      /* not json */
    }
    throw new ApiError(res.status, msg);
  }
  return (await res.json()) as T;
}

async function authedPutEmpty(path: string): Promise<void> {
  const token = getToken();
  const res = await fetch(path, {
    method: "PUT",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok && res.status !== 204) {
    let msg = `${res.status} ${res.statusText}`;
    try {
      const data = await res.json();
      if (data?.error?.message) msg = data.error.message;
      else if (data?.detail) msg = data.detail;
    } catch {
      /* not json */
    }
    throw new ApiError(res.status, msg);
  }
}

export const sources = {
  list: () => api.get<Source[]>("/v1/sources", { auth: true }),
  get: (id: string) => api.get<Source>(`/v1/sources/${id}`, { auth: true }),
  upload: (file: File, title?: string) =>
    authedUpload<Source>("/v1/sources", file, title ? { title } : undefined),
  rename: (id: string, title: string) =>
    authedPatch<Source>(`/v1/sources/${id}`, { title }),
  remove: (id: string) => authedDelete(`/v1/sources/${id}`),
  listForConversation: (conversationId: string) =>
    api.get<Source[]>(`/v1/conversations/${conversationId}/sources`, { auth: true }),
  attach: (conversationId: string, sourceId: string) =>
    authedPutEmpty(`/v1/conversations/${conversationId}/sources/${sourceId}`),
  detach: (conversationId: string, sourceId: string) =>
    authedDelete(`/v1/conversations/${conversationId}/sources/${sourceId}`),
};

export type Assistant = {
  id: string;
  user_id: string;
  name: string;
  description: string | null;
  instructions: string | null;
  model: string | null;
  created_at: string;
  updated_at: string;
};

export const assistants = {
  list: () => api.get<Assistant[]>("/v1/assistants", { auth: true }),
  get: (id: string) => api.get<Assistant>(`/v1/assistants/${id}`, { auth: true }),
  create: (body: { name: string; description?: string; instructions?: string; model?: string }) =>
    api.post<Assistant>("/v1/assistants", body, { auth: true }),
  update: (
    id: string,
    body: Partial<{ name: string; description: string; instructions: string; model: string }>,
  ) => authedPatch<Assistant>(`/v1/assistants/${id}`, body),
  remove: (id: string) => authedDelete(`/v1/assistants/${id}`),
};

export type LibraryKind = "conversation" | "image" | "audio";

export type LibraryItem = {
  id: string;
  kind: LibraryKind;
  title: string;
  created_at: string;
  preview: string | null;
  model: string | null;
};

export const library = {
  list: (kind?: LibraryKind) => {
    const qs = kind ? `?kind=${kind}` : "";
    return api.get<LibraryItem[]>(`/v1/library${qs}`, { auth: true });
  },
};

export type Memory = {
  id: string;
  kind: "fact" | "preference" | "note";
  content: string;
  created_at: string;
  updated_at: string;
};

export const memories = {
  list: () => api.get<Memory[]>("/v1/memories", { auth: true }),
  create: (body: { content: string; kind?: Memory["kind"] }) =>
    api.post<Memory>("/v1/memories", body, { auth: true }),
  remove: (id: string) => authedDelete(`/v1/memories/${id}`),
};

export type GeneratedImage = {
  id: string;
  prompt: string;
  model: string;
  size: string;
  conversation_id: string | null;
  created_at: string;
};

export const images = {
  list: () => api.get<GeneratedImage[]>("/v1/images", { auth: true }),
  bytesUrl: (id: string) => `/v1/images/${id}/bytes`,
  remove: (id: string) => authedDelete(`/v1/images/${id}`),
};

export type GeneratedAudio = {
  id: string;
  text: string;
  model: string;
  voice: string;
  conversation_id: string | null;
  created_at: string;
};


export const audio = {
  list: () => api.get<GeneratedAudio[]>("/v1/audio", { auth: true }),
  bytesUrl: (id: string) => `/v1/audio/${id}/bytes`,
  synthesize: (body: { text: string; voice?: string; model?: string }) =>
    api.post<GeneratedAudio>("/v1/voiceover", body, { auth: true }),
  remove: (id: string) => authedDelete(`/v1/audio/${id}`),
};

export type Preset = {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  category: string;
  input_schema: Record<string, unknown>;
  prompt_template: string;
  model: string | null;
  enabled: boolean;
  position: number;
  created_at: string;
  updated_at: string;
};

export const presets = {
  list: () => api.get<Preset[]>("/v1/presets", { auth: true }),
  get: (id: string) => api.get<Preset>(`/v1/presets/${id}`, { auth: true }),
  run: (id: string, values: Record<string, unknown>) =>
    api.post<{ conversation_id: string }>(`/v1/presets/${id}/run`, { values }, { auth: true }),
};

export const account = {
  changePassword: (body: { current_password: string; new_password: string }) =>
    api.post<void>("/v1/auth/change-password", body, { auth: true }),
};
